import { Fragment, useEffect, useMemo } from 'react';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { onboardingAdmin as rules } from 'further-ui/validations';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import { styled, Button, Box } from '@mui/material';
import TextField from '../StyledComponents/TextField';
import CountrySelector from '../StyledComponents/CountrySelector';
import {
  Country,
  CrsClassificationLabels,
  FatcaClassificationLabels,
} from 'further-ui/labels';
import {
  CrsClassification,
  FatcaClassification,
} from 'further-types/lp/investor-tax-details';
import BasicSelect from '../StyledComponents/BasicSelect';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import { AccountType } from 'further-types/investor';

const ButtonContainer = styled(Box)({
  textAlign: 'right',
});

const ButtonContainerLeft = styled(Box)({
  textAlign: 'left',
});

const fatcaClassificationOptions = Object.values(FatcaClassification).map(
  (classification) => ({
    value: classification,
    label: FatcaClassificationLabels[classification],
  }),
);

const individualFatcaClassificationOptions = [
  FatcaClassification.UsResident,
  FatcaClassification.FormerUsResident,
  FatcaClassification.NonUsResident,
];

const crsClassificationOptions = Object.values(CrsClassification).map(
  (classification) => ({
    value: classification,
    label: CrsClassificationLabels[classification],
  }),
);

const individualCrsClassificationOptions = [
  CrsClassification.UsResident,
  CrsClassification.FormerUsResident,
  CrsClassification.NonUsResident,
];

type Props = {
  vehicleApplicationId: string;
  vehicleId: string;
  isActive: boolean;
  onSave: () => void;
};

const TaxInformation: React.FC<Props> = ({
  isActive,
  vehicleApplicationId,
  vehicleId,
  onSave,
}) => {
  type FormValues = z.infer<typeof rules.SaveTaxInformationInput>;

  const [canEdit] = useHasPermissions([RolePermission.LpEditCommitment]);
  const { taxDetails, saveTaxDetails, isPending, investor } =
    useVehicleApplication(vehicleId, vehicleApplicationId);

  const defaultValues = useMemo(() => {
    return {
      vehicleApplicationId,
      taxResidencies: taxDetails?.taxResidencies ?? [
        {
          country: undefined,
          taxIdentificationNumber: '',
        },
      ],
      fatcaClassification:
        taxDetails?.fatcaClassification?.classification ?? undefined,
      crsClassification:
        taxDetails?.crsClassification?.classification ?? undefined,
    };
  }, [taxDetails, vehicleApplicationId]);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(rules.SaveTaxInformationInput),
    defaultValues,
  });

  const {
    fields: taxResidencies,
    append,
    remove,
  } = useFieldArray({
    name: 'taxResidencies',
    control,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [isActive, reset, defaultValues]);

  const onSubmit = (values: FormValues) => {
    if (isPending) {
      return;
    }
    saveTaxDetails(
      {
        vehicleId,
        ...values,
      },
      {
        onSuccess: onSave,
      },
    );
  };

  const isLegalEntity = investor.accountType === AccountType.LegalEntity;
  const availableFatcaClassificationOptions = fatcaClassificationOptions.filter(
    (option) =>
      option.value === defaultValues.fatcaClassification ||
      (isLegalEntity
        ? !individualFatcaClassificationOptions.includes(option.value)
        : individualFatcaClassificationOptions.includes(option.value)),
  );
  const availableCrsClassificationOptions = crsClassificationOptions.filter(
    (option) =>
      option.value === defaultValues.crsClassification ||
      (isLegalEntity
        ? !individualCrsClassificationOptions.includes(option.value)
        : individualCrsClassificationOptions.includes(option.value)),
  );

  return (
    <Column fullWidth spacing="lg">
      <FormField label="FATCA classification">
        <Controller
          name={`fatcaClassification`}
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <BasicSelect
              {...field}
              size="small"
              fullWidth
              placeholder="FATCA classification"
              error={!!error}
              helperText={error?.message}
              options={availableFatcaClassificationOptions}
            />
          )}
        />
      </FormField>
      <FormField label="CRS classification">
        <Controller
          name={`crsClassification`}
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <BasicSelect
              {...field}
              size="small"
              fullWidth
              placeholder="CRS classification"
              error={!!error}
              helperText={error?.message}
              options={availableCrsClassificationOptions}
            />
          )}
        />
      </FormField>
      {taxResidencies.map((taxResidency, index) => (
        <Fragment key={taxResidency.id}>
          <FormField label="Tax residency/domicile">
            <Controller
              name={`taxResidencies.${index}.country`}
              disabled={!isActive}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <CountrySelector
                  {...field}
                  placeholder="Tax residency/domicile"
                  error={error}
                  disabled={!isActive}
                />
              )}
            />
          </FormField>
          <FormField label="Tax identification number">
            <Controller
              name={`taxResidencies.${index}.taxIdentificationNumber`}
              disabled={!isActive}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Tax identification number"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          {isActive && canEdit && index > 0 ? (
            <ButtonContainer>
              <Button
                variant="outlined"
                size="large"
                color="error"
                onClick={() => {
                  remove(index);
                }}
              >
                Remove
              </Button>
            </ButtonContainer>
          ) : null}
        </Fragment>
      ))}

      {isActive && canEdit ? (
        <>
          <ButtonContainerLeft>
            <Button
              variant="outlined"
              size="large"
              color="primary"
              onClick={() => {
                append({
                  country: Country.UnitedKingdom,
                  taxIdentificationNumber: '',
                });
              }}
            >
              Add another
            </Button>
          </ButtonContainerLeft>
          <ButtonContainer>
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              size="large"
              disabled={isPending}
            >
              Save
            </Button>
          </ButtonContainer>
        </>
      ) : null}
    </Column>
  );
};

export default TaxInformation;
